import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { t } from "../i18n";
import { Home, Briefcase, Mail, CircleCheck, ArrowLeft } from "lucide-react";

export default function AuthPage() {
  const location = useLocation();
  const initialTab = new URLSearchParams(location.search).get("tab") || "login";

  const [tab,     setTab]     = useState(initialTab);
  const [perfil,  setPerfil]  = useState("");
  const [name,    setName]    = useState("");
  const [email,   setEmail]   = useState("");
  const [pass,    setPass]    = useState("");
  const [pass2,   setPass2]   = useState("");
  const [lang,    setLang]    = useState("es");
  const [error,   setError]   = useState("");
  const [msg,     setMsg]     = useState("");
  const [loading, setLoading] = useState(false);
  const [pendingVerification, setPendingVerification] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(false);

  const { login, register, resetPassword, resendVerification, checkVerification, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  const T = (key) => t(lang, key);

  // Si cambia el parámetro de URL, actualizar el tab
  useEffect(() => {
    const tab = new URLSearchParams(location.search).get("tab");
    if (tab === "register" || tab === "login") setTab(tab);
  }, [location.search]);

  async function handleLogin(e) {
    e.preventDefault(); setError(""); setLoading(true);
    try {
      await login(email, pass);
      navigate("/panel");
    } catch(err) {
      if (err.code === "auth/email-not-verified") {
        setError(lang === "en"
          ? "Please verify your email before signing in. Check your inbox."
          : "Por favor verifica tu email antes de entrar. Revisa tu bandeja de entrada.");
      } else {
        setError(T("auth_err_login"));
      }
    }
    setLoading(false);
  }

  async function handleRegister(e) {
    e.preventDefault(); setError("");
    if (!perfil)         return setError(T("auth_err_profile"));
    if (!name.trim())    return setError(T("auth_err_name"));
    if (pass !== pass2)  return setError(T("auth_err_pass_match"));
    if (pass.length < 6) return setError(T("auth_err_pass_length"));
    setLoading(true);
    try {
      await register(email, pass, name, perfil, lang);
      setPendingVerification(true);
    } catch (err) {
      if (err.code === "auth/email-already-in-use") setError(T("auth_err_email_used"));
      else setError(T("auth_err_generic"));
    }
    setLoading(false);
  }

  async function handleGoogleLogin() {
    setError(""); setLoading(true);
    try {
      await loginWithGoogle(lang);
      navigate("/panel");
    } catch (err) {
      // El usuario cerrando el popup sin elegir cuenta no es un error real
      if (err.code !== "auth/popup-closed-by-user" && err.code !== "auth/cancelled-popup-request") {
        setError(lang === "en" ? "Error signing in with Google." : "Error al acceder con Google.");
      }
    }
    setLoading(false);
  }

  async function handleResend() {
    if (resendCooldown) return;
    try {
      await resendVerification();
      setResendCooldown(true);
      setMsg(lang === "en" ? "Email resent. Check your inbox." : "Email reenviado. Revisa tu bandeja.");
      setTimeout(() => { setResendCooldown(false); setMsg(""); }, 60000);
    } catch {
      setError(lang === "en" ? "Error resending email." : "Error al reenviar el email.");
    }
  }

  async function handleCheckVerification() {
    setLoading(true);
    try {
      const verified = await checkVerification();
      if (verified) {
        navigate("/panel");
      } else {
        setError(lang === "en"
          ? "Email not verified yet. Check your inbox and click the link."
          : "Email aún no verificado. Revisa tu bandeja y haz clic en el enlace.");
      }
    } catch {
      setError(lang === "en" ? "Error checking verification." : "Error al comprobar la verificación.");
    }
    setLoading(false);
  }

  async function handleReset(e) {
    e.preventDefault(); setError(""); setMsg("");
    if (!email) return setError(T("auth_err_no_email"));
    try {
      await resetPassword(email);
      setMsg(T("auth_reset_sent"));
    } catch { setError(T("auth_err_email")); }
  }

  if (pendingVerification) {
    return (
      <div className="auth-wrap">
        <div className="auth-card">
          <div style={{textAlign:"center",padding:"20px 0"}}>
            <div style={{marginBottom:16,display:"flex",justifyContent:"center"}}><Mail size={48}/></div>
            <h2 style={{fontSize:22,fontWeight:800,color:"var(--gray-900)",marginBottom:8}}>
              {lang === "en" ? "Verify your email" : "Verifica tu email"}
            </h2>
            <p style={{fontSize:14,color:"var(--gray-500)",marginBottom:24,lineHeight:1.6}}>
              {lang === "en"
                ? `We've sent a verification link to ${email}. Click it to activate your account.`
                : `Hemos enviado un enlace de verificación a ${email}. Haz clic en él para activar tu cuenta.`}
            </p>

            {error && <div className="alert alert-error">{error}</div>}
            {msg   && <div className="alert alert-success">{msg}</div>}

            <button className="btn btn-primary"
              style={{width:"100%",justifyContent:"center",padding:"12px",marginBottom:12,display:"flex",alignItems:"center",gap:6}}
              onClick={handleCheckVerification}
              disabled={loading}>
              {loading ? "..." : <><CircleCheck size={15}/> {lang === "en" ? "I've verified my email" : "Ya verifiqué mi email"}</>}
            </button>

            <button
              onClick={handleResend}
              disabled={resendCooldown}
              style={{background:"none",border:"none",color:resendCooldown?"var(--gray-400)":"var(--blue)",
                      cursor:resendCooldown?"not-allowed":"pointer",fontSize:13,marginBottom:16}}>
              {resendCooldown
                ? (lang === "en" ? "Email sent (wait 1 min)" : "Email enviado (espera 1 min)")
                : (lang === "en" ? "Resend verification email" : "Reenviar email de verificación")}
            </button>

            <p style={{fontSize:12,color:"var(--gray-400)"}}>
              <button type="button"
                style={{background:"none",border:"none",color:"var(--blue)",cursor:"pointer",fontSize:12,display:"inline-flex",alignItems:"center",gap:4}}
                onClick={()=>{ setPendingVerification(false); setTab("login"); setError(""); }}>
                <ArrowLeft size={12}/> {lang === "en" ? "Back to login" : "Volver al login"}
              </button>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-wrap">
      <div className="auth-card">
        <div className="auth-logo">
          <h1>{T("auth_title")}</h1>
          <p>{T("auth_subtitle")}</p>
        </div>

        {/* Language selector */}
        <div style={{display:"flex",justifyContent:"center",gap:8,marginBottom:16}}>
          {[["es","Español"],["en","English"]].map(([code,label])=>(
            <button key={code} type="button"
              onClick={()=>setLang(code)}
              style={{padding:"6px 14px",borderRadius:20,border:`2px solid ${lang===code?"var(--blue)":"var(--border)"}`,
                      background:lang===code?"var(--blue-l)":"white",color:lang===code?"var(--blue)":"var(--gray-600)",
                      fontSize:13,fontWeight:500,cursor:"pointer"}}>
              {label}
            </button>
          ))}
        </div>

        {tab !== "reset" && (
          <>
            <button type="button" onClick={handleGoogleLogin} disabled={loading}
              style={{width:"100%",display:"flex",alignItems:"center",justifyContent:"center",gap:10,
                      padding:"10px 14px",borderRadius:8,border:"1px solid var(--border)",
                      background:"white",color:"var(--gray-700)",fontSize:14,fontWeight:500,
                      cursor:"pointer",marginBottom:16}}>
              <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                <path fill="#4285F4" d="M17.64 9.2045c0-.6381-.0573-1.2527-.1636-1.8409H9v3.4814h4.8436c-.2086 1.125-.8427 2.0782-1.7959 2.7164v2.2581h2.9087c1.7018-1.5668 2.6836-3.874 2.6836-6.6155z"/>
                <path fill="#34A853" d="M9 18c2.43 0 4.4673-.8059 5.9573-2.1805l-2.9087-2.2581c-.8064.5409-1.8368.8632-3.0486.8632-2.3445 0-4.3282-1.5832-5.0359-3.7104H.9573v2.3318C2.4382 16.0182 5.4818 18 9 18z"/>
                <path fill="#FBBC05" d="M3.9641 10.71c-.1818-.5409-.2864-1.1182-.2864-1.71s.1046-1.1691.2864-1.71V4.9582H.9573C.3477 6.1731 0 7.5477 0 9s.3477 2.8268.9573 4.0418L3.9641 10.71z"/>
                <path fill="#EA4335" d="M9 3.5795c1.3214 0 2.5077.4541 3.4405 1.346l2.5814-2.5814C13.4632.8918 11.43 0 9 0 5.4818 0 2.4382 1.9818.9573 4.9582L3.9641 7.29C4.6718 5.1627 6.6555 3.5795 9 3.5795z"/>
              </svg>
              {lang === "en" ? "Continue with Google" : "Continuar con Google"}
            </button>
            <div style={{display:"flex",alignItems:"center",gap:10,margin:"0 0 16px"}}>
              <div style={{flex:1,height:1,background:"var(--border)"}} />
              <span style={{fontSize:12,color:"var(--gray-400)"}}>{lang==="en"?"or":"o"}</span>
              <div style={{flex:1,height:1,background:"var(--border)"}} />
            </div>
          </>
        )}

        {tab !== "reset" && (
          <div className="auth-tabs">
            <button className={`auth-tab ${tab==="login"?"active":""}`} onClick={()=>{setTab("login");setError("");}}>
              {T("auth_login")}
            </button>
            <button className={`auth-tab ${tab==="register"?"active":""}`} onClick={()=>{setTab("register");setError("");}}>
              {T("auth_register")}
            </button>
          </div>
        )}

        {error && <div className="alert alert-error">{error}</div>}
        {msg   && <div className="alert alert-success">{msg}</div>}

        {/* ── LOGIN ── */}
        {tab === "login" && (
          <form onSubmit={handleLogin}>
            <div className="form-group">
              <label className="form-label">{T("auth_email")}</label>
              <input className="form-input" type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="tu@email.com" required />
            </div>
            <div className="form-group">
              <label className="form-label">{T("auth_password")}</label>
              <input className="form-input" type="password" value={pass} onChange={e=>setPass(e.target.value)} placeholder="••••••••" required />
            </div>
            <button className="btn btn-primary" style={{width:"100%",justifyContent:"center",marginTop:4}} disabled={loading}>
              {loading ? T("auth_logging_in") : T("auth_login_btn")}
            </button>
            <p style={{textAlign:"center",marginTop:14,fontSize:13,color:"var(--gray-500)"}}>
              <button type="button" style={{background:"none",border:"none",color:"var(--blue)",cursor:"pointer",fontSize:13}}
                onClick={()=>{setTab("reset");setError("");setMsg("");}}>
                {T("auth_forgot")}
              </button>
            </p>
          </form>
        )}

        {/* ── REGISTER ── */}
        {tab === "register" && (
          <form onSubmit={handleRegister}>
            <div className="form-group">
              <label className="form-label">{T("auth_profile_label")}</label>
              <div className="profile-grid">
                <div className={`profile-card ${perfil==="personal"?"selected":""}`} onClick={()=>setPerfil("personal")}>
                  <div className="emoji" style={{display:"flex",justifyContent:"center"}}><Home size={28}/></div>
                  <h3>{T("auth_profile_personal")}</h3>
                  <p>{T("auth_profile_personal_desc")}</p>
                </div>
                <div className={`profile-card ${perfil==="negocio"?"selected":""}`} onClick={()=>setPerfil("negocio")}>
                  <div className="emoji" style={{display:"flex",justifyContent:"center"}}><Briefcase size={28}/></div>
                  <h3>{T("auth_profile_business")}</h3>
                  <p>{T("auth_profile_business_desc")}</p>
                </div>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">{T("auth_name")}</label>
              <input className="form-input" type="text" value={name} onChange={e=>setName(e.target.value)} placeholder={T("auth_name_placeholder")} required />
            </div>
            <div className="form-group">
              <label className="form-label">{T("auth_email")}</label>
              <input className="form-input" type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="tu@email.com" required />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{T("auth_password")}</label>
                <input className="form-input" type="password" value={pass} onChange={e=>setPass(e.target.value)} placeholder={T("auth_min_chars")} required />
              </div>
              <div className="form-group">
                <label className="form-label">{T("auth_password_repeat")}</label>
                <input className="form-input" type="password" value={pass2} onChange={e=>setPass2(e.target.value)} placeholder="••••••••" required />
              </div>
            </div>
            <button className="btn btn-primary" style={{width:"100%",justifyContent:"center",marginTop:4}} disabled={loading}>
              {loading ? T("auth_registering") : T("auth_register_btn")}
            </button>
            <p style={{textAlign:"center",marginTop:12,fontSize:12,color:"var(--gray-400)"}}>
              {T("auth_trial_note")}
            </p>
            <p style={{textAlign:"center",marginTop:8,fontSize:11,color:"var(--gray-400)"}}>
              {lang==="en" ? "By registering you accept our " : "Al registrarte aceptas nuestros "}
              <a href="/legal" target="_blank" style={{color:"var(--blue)",textDecoration:"none"}}>
                {lang==="en" ? "Terms & Conditions" : "Términos y Condiciones"}
              </a>
              {" "}{lang==="en" ? "and" : "y"}{" "}
              <a href="/legal" target="_blank" style={{color:"var(--blue)",textDecoration:"none"}}>
                {lang==="en" ? "Privacy Policy" : "Política de Privacidad"}
              </a>
            </p>
          </form>
        )}

        {/* ── RESET PASSWORD ── */}
        {tab === "reset" && (
          <form onSubmit={handleReset}>
            <p style={{fontSize:14,color:"var(--gray-600)",marginBottom:16}}>{T("auth_reset_title")}</p>
            <div className="form-group">
              <label className="form-label">{T("auth_email")}</label>
              <input className="form-input" type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="tu@email.com" required />
            </div>
            <button className="btn btn-primary" style={{width:"100%",justifyContent:"center"}} type="submit">
              {T("auth_reset_btn")}
            </button>
            <p style={{textAlign:"center",marginTop:14,fontSize:13}}>
              <button type="button" style={{background:"none",border:"none",color:"var(--blue)",cursor:"pointer",fontSize:13}}
                onClick={()=>{setTab("login");setError("");setMsg("");}}>
                {T("auth_reset_back")}
              </button>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
